
import React, { memo, useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCategories } from "@/utils/categories";

interface CategoryFilterProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export const CategoryFilter = memo(({ selectedCategory, onCategoryChange }: CategoryFilterProps) => {
  const [categories, setCategories] = useState<Array<{
    id: string;
    label: string;
    icon: React.ElementType;
    description: string;
    imageUrl?: string;
  }>>([]);

  useEffect(() => {
    // Charger les catégories au montage du composant
    const loadedCategories = getCategories();
    console.log("Catégories chargées dans CategoryFilter:", loadedCategories);
    setCategories(loadedCategories);
    
    // Mettre à jour les catégories si elles changent dans le localStorage
    const handleStorageChange = () => {
      setCategories(getCategories());
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (categories.length === 0) {
    return <div className="text-sm text-gray-500">Chargement des catégories...</div>;
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        Filtrer par catégorie
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === "" ? "default" : "outline"}
          onClick={() => onCategoryChange("")}
          className="flex items-center gap-2 group transition-all duration-200"
        >
          Toutes les catégories
        </Button>
        
        {categories.map(({ id, label, icon: Icon, description, imageUrl }) => {
          // Créer le bouton avec tooltip séparé pour éviter l'imbrication des boutons
          return (
            <div key={id} className="relative inline-flex">
              <Button
                variant={selectedCategory === id ? "default" : "outline"}
                onClick={() => onCategoryChange(id)}
                className="flex items-center gap-2 group transition-all duration-200 relative overflow-hidden"
              >
                {imageUrl && (
                  <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity">
                    <img src={imageUrl} alt={label} className="w-full h-full object-cover" />
                  </div>
                )}
                {Icon && <Icon className="h-4 w-4 group-hover:scale-110 transition-transform relative z-10" />}
                <span className="relative z-10">{label}</span>
              </Button>
              
              {description && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="ml-1 inline-flex items-center absolute right-1 top-1/2 -mt-1.5 z-10">
                        <HelpCircle className="h-3 w-3 text-gray-500 cursor-help" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white p-2 shadow-lg rounded-md border max-w-xs break-words whitespace-normal">
                      <p>{description}</p>
                      {imageUrl && (
                        <div className="mt-2">
                          <img src={imageUrl} alt={label} className="w-full h-auto rounded-md" />
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

CategoryFilter.displayName = 'CategoryFilter';
